import { apiClient } from './api';

export interface PackageServiceItem {
  serviceId: string;
  quantity: number;
}

export interface CreatePackageData {
  name: string;
  description: string;
  services: PackageServiceItem[];
  discountPercentage: number;
  validityDays: number;
  isActive: boolean;
}

export interface UpdatePackageData extends Partial<CreatePackageData> {
  id: string;
}

export interface ServicePackageResponse {
  id: string;
  name: string;
  description: string;
  totalPrice: number;
  discountedPrice: number;
  discountPercentage: number;
  validityDays: number;
  isActive: boolean;
  packageServices: {
    id: string;
    quantity: number;
    service: {
      id: string;
      name: string;
      price: number;
      duration: number;
      category: string;
    };
  }[];
  createdAt: string;
  updatedAt: string;
}

export interface PackagesResponse {
  packages: ServicePackageResponse[];
  total: number;
  page: number;
  limit: number;
}

export interface PackageFilters {
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

class PackageApiService {
  private baseUrl = '/packages';

  async getPackages(filters: PackageFilters = {}): Promise<PackagesResponse> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    if (filters.page !== undefined) params.append('page', filters.page.toString());
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());

    const response = await apiClient.get<any>(`${this.baseUrl}?${params.toString()}`);
    const data = response?.data ?? response;
    const packages = data?.packages ?? [];
    return {
      packages,
      total: packages.length,
      page: filters.page ?? 1,
      limit: filters.limit ?? 20,
    };
  }

  async getPackageById(id: string): Promise<ServicePackageResponse> {
    const response = await apiClient.get<any>(`${this.baseUrl}/${id}`);
    const data = response?.data ?? response;
    return data?.package ?? data;
  }

  async createPackage(data: CreatePackageData): Promise<ServicePackageResponse> {
    const response = await apiClient.post<any>(this.baseUrl, data);
    const resData = response?.data ?? response;
    return resData?.package ?? resData;
  }

  async updatePackage(data: UpdatePackageData): Promise<ServicePackageResponse> {
    const { id, ...updateData } = data;
    const response = await apiClient.put<any>(`${this.baseUrl}/${id}`, updateData);
    const resData = response?.data ?? response;
    return resData?.package ?? resData;
  }

  async deletePackage(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async calculatePrice(services: PackageServiceItem[]): Promise<{
    totalPrice: number;
    discountedPrice: number;
    discountAmount: number;
  }> {
    const response = await apiClient.post(`${this.baseUrl}/calculate-price`, { services });
    return response.data;
  }
}

export const packageService = new PackageApiService();
